<template>
  <div class="login-container">
    <div class="login-wrapper">
      <!-- 左侧：微信扫码登录 -->
      <div class="login-left">
        <div class="qr-section">
          <!-- 二维码容器：微信 JS SDK 会在这里插入 iframe -->
          <div class="qrcode-container">
            <div v-if="qrcodeLoading" class="qrcode-loading">
              <el-icon class="is-loading"><Loading /></el-icon>
              <p>正在加载二维码...</p>
            </div>
            <!-- 微信 WxLogin SDK 会在这个 div 中生成二维码 -->
            <div id="wx_login_container" class="wx-iframe-container"></div>
          </div>
          
          <!-- 状态提示 -->
          <div class="qr-status-tips">
            <p v-if="scanStatus === 'scanned'" class="tip success">
              <el-icon><CircleCheck /></el-icon> 已扫码，请在手机端确认授权
            </p>
            <p v-if="scanStatus === 'expired'" class="tip error">
              二维码已过期，<span @click="refreshQrcode" class="link">点击刷新</span>
            </p>
          </div>
          
          <!-- 刷新按钮 -->
          <el-button 
            v-if="!qrcodeLoading && (scanStatus === 'pending' || scanStatus === 'expired' || scanStatus === 'scanned')" 
            type="text" 
            @click="refreshQrcode"
            class="refresh-btn"
          >
            刷新二维码
          </el-button>
        </div>
      </div>

      <!-- 右侧：账号登录表单 -->
      <div class="login-right">
        <div class="form-section">
          <h3 class="form-title">账密登录</h3>
          
          <!-- 账密登录表单 -->
          <el-form
            ref="passwordFormRef"
            :model="passwordForm"
            :rules="passwordRules"
            class="login-form"
            @submit.prevent="handlePasswordLogin"
          >
            <el-form-item prop="username">
              <el-input
                v-model="passwordForm.username"
                placeholder="请输入账号名"
                size="large"
                :prefix-icon="User"
              />
            </el-form-item>
            <el-form-item prop="password">
              <el-input
                v-model="passwordForm.password"
                type="password"
                placeholder="请输入密码"
                size="large"
                :prefix-icon="Lock"
                show-password
                @keyup.enter="handlePasswordLogin"
              />
            </el-form-item>
            <el-form-item>
              <el-button
                type="primary"
                size="large"
                style="width: 100%"
                :loading="passwordLoading"
                @click="handlePasswordLogin"
              >
                立即登录
              </el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { Loading, User, Lock, CircleCheck } from '@element-plus/icons-vue';
import { login } from '../api/auth';
import api from '../api/config';

export default {
  name: 'LoginPage',
  components: {
    Loading,
    CircleCheck
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    
    // 二维码相关（使用微信官方 WxLogin SDK）
    const qrcodeLoading = ref(false);
    const loginToken = ref('');
    const scanStatus = ref('pending');
    const scanHint = ref('');
    let statusPollingTimer = null;
    
    // 账密登录表单
    const passwordForm = reactive({
      username: '',
      password: ''
    });
    const passwordRules = {
      username: [
        { required: true, message: '请输入账号名', trigger: 'blur' }
      ],
      password: [
        { required: true, message: '请输入密码', trigger: 'blur' }
      ]
    };
    const passwordLoading = ref(false);
    const passwordFormRef = ref();
    
    // 获取登录后要跳转的页面
    const getRedirectPath = () => {
      const redirect = route.query.redirect;
      if (redirect && typeof redirect === 'string') {
        return redirect.startsWith('/') ? redirect : `/${redirect}`;
      }
      return '/dashboard';
    };
    
    // 动态加载微信 JS SDK
    const loadWxScript = () => {
      return new Promise((resolve, reject) => {
        if (window.WxLogin) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = 'https://res.wx.qq.com/connect/zh_CN/htmledition/js/wxLogin.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('微信 SDK 加载失败'));
        document.head.appendChild(script);
      });
    };

    // 初始化微信登录二维码（使用官方 WxLogin SDK）
    const generateQrcode = async () => {
      qrcodeLoading.value = true;
      scanStatus.value = 'pending';
      scanHint.value = '';
      
      // 清空容器
      const container = document.getElementById('wx_login_container');
      if (container) {
        container.innerHTML = '';
      }
      
      try {
        // 请求后端获取 appid, redirect_uri, token
        const resp = await api.post('/auth/wechat/scan-login/generate');
        if (!resp.success) {
          throw new Error(resp.message || '生成二维码失败');
        }

        const { appid, redirect_uri, login_token } = resp.data;
        loginToken.value = login_token;

        console.log('✅ 获取到登录参数:', { appid, redirect_uri, login_token });

        // 确保微信 JS SDK 已加载
        await loadWxScript();

        // 自定义样式（Base64 编码的 CSS）
        // 隐藏微信默认的标题、边框等，只显示干净的二维码
        const customStyle = "data:text/css;base64,LmltcG93ZXJCb3ggLnFyY29kZSB7d2lkdGg6IDE4MHB4O21hcmdpbi10b3A6IDBweDt9Ci5pbXBvd2VyQm94IC50aXRsZSB7ZGlzcGxheTogbm9uZTt9Ci5pbXBvd2VyQm94IC5pbmZvIHtkaXNwbGF5OiBub25lO30KLmltcG93ZXJCb3ggLnN0YXR1c19pY29uIHtkaXNwbGF5OiBub25lO30KLmltcG93ZXJCb3ggLmltcG93ZXJCb3hfYmQge2JvcmRlcjogbm9uZTt9";

        // 实例化 WxLogin
        new window.WxLogin({
          self_redirect: false, // false: 在 iframe 内跳转; true: 整个页面跳转
          id: "wx_login_container",
          appid: appid,
          scope: "snsapi_login",
          redirect_uri: encodeURIComponent(redirect_uri),
          state: login_token,
          style: "black", // 二维码样式：black 或 white
          href: customStyle // 加载自定义样式，隐藏标题和边框
        });

        console.log('✅ 微信登录二维码已生成');

        qrcodeLoading.value = false;

        // 开始轮询状态（作为兜底）
        startStatusPolling();
      } catch (error) {
        console.error('初始化微信登录失败:', error);
        ElMessage.error('无法加载微信登录，请刷新重试');
        qrcodeLoading.value = false;
      }
    };
    
    // 刷新二维码
    const refreshQrcode = () => {
      generateQrcode();
    };

    // 轮询扫码登录状态（主要机制，不依赖 postMessage）
    const startStatusPolling = () => {
      // 清除之前的定时器
      if (statusPollingTimer) {
        clearInterval(statusPollingTimer);
      }
      
      if (!loginToken.value) {
        console.error('❌ 无法启动轮询：loginToken 为空');
        return;
      }
      
      console.log('🔄 开始轮询登录状态，loginToken:', loginToken.value);
      
      let pollCount = 0;
      const maxPollCount = 300; // 最多轮询5分钟（300秒）
      
      statusPollingTimer = setInterval(async () => {
        if (!loginToken.value) {
          console.warn('⚠️ 轮询跳过：loginToken 为空');
          return;
        }
        
        pollCount++;
        if (pollCount > maxPollCount) {
          console.log('⏰ 轮询超时，停止轮询');
          clearInterval(statusPollingTimer);
          statusPollingTimer = null;
          ElMessage.warning('登录超时，请刷新二维码重试');
          return;
        }
        
        try {
          const resp = await api.get('/auth/wechat/scan-login/status', {
            params: {
              login_token: loginToken.value,
              _t: Date.now() // 防止 Safari 缓存 GET，导致轮询一直拿到旧的 pending
            }
          });
          
          // 每次轮询都打印完整响应（便于调试）
          console.log(`[轮询 ${pollCount}] 完整响应:`, JSON.stringify(resp, null, 2));
          
          if (resp && resp.success) {
            const data = resp.data || {};
            scanStatus.value = data.status;
            scanHint.value = data.hint || '';
            
            console.log(`[轮询 ${pollCount}] 扫码登录状态:`, { 
              loginToken: loginToken.value,
              status: data.status, 
              hasToken: !!data.token,
              hasUser: !!data.user,
              user_id: data.user_id,
              token: data.token ? data.token.substring(0, 20) + '...' : null,
              fullData: JSON.stringify(data, null, 2)
            });
            
            // 如果已确认，完成登录（只要有 token 就认为成功）
            if (data.token) {
              console.log('✅ 检测到登录成功，准备跳转');
              clearInterval(statusPollingTimer);
              statusPollingTimer = null;
              
              // 先保存 token 和用户信息（同步操作，确保立即生效）
              localStorage.setItem('token', data.token);
              if (data.user) {
                localStorage.setItem('user', JSON.stringify(data.user));
                console.log('✅ 用户信息已保存:', data.user);
              } else {
                localStorage.removeItem('user');
              }
              
              // 触发 localStorageChange 事件，通知 App.vue 更新状态
              window.dispatchEvent(new Event('localStorageChange'));
              
              // 等待 App.vue 的 updateUserInfo 完成，确保用户信息已加载
              // 这样可以避免 EmotionMarket 组件挂载时 getCurrentUser 失败
              await new Promise(resolve => setTimeout(resolve, 500));
              
              ElMessage.success('微信登录成功');
              
              // 扫码登录统一直接跳转到表情页面（使用 router.push 避免整页重载）
              console.log('🚀 开始跳转到 /market，当前 token:', localStorage.getItem('token') ? '已保存' : '未保存');
              router.push('/market').catch(err => {
                console.warn('路由跳转失败，使用 window.location:', err);
                window.location.href = '/market';
              });
              return; // 确保不再继续执行
            } 
            
            // 如果状态是 confirmed 但没有 token，可能是后端还没生成，继续等待
            if (data.status === 'confirmed' && !data.token) {
              console.warn('⚠️ 状态已确认但未返回token，继续等待...', data);
            } else if (data.status === 'expired') {
              console.log('⏰ 二维码已过期，停止轮询');
              clearInterval(statusPollingTimer);
              statusPollingTimer = null;
            }
          } else {
            console.warn('⚠️ 轮询响应格式异常:', resp);
          }
        } catch (error) {
          console.error('❌ 查询登录状态失败:', error);
          // 网络错误时不停止轮询，继续尝试
        }
      }, 1000); // 每1秒轮询一次
    };
    
    // 账密登录
    const handlePasswordLogin = async () => {
      try {
        if (!passwordFormRef.value) {
          ElMessage.error('表单初始化失败');
          return;
        }
        
        await passwordFormRef.value.validate();
        passwordLoading.value = true;
        
        const response = await login(passwordForm);
        
        if (response.success) {
          localStorage.setItem('token', response.token);
          if (response.user) {
            localStorage.setItem('user', JSON.stringify(response.user));
          } else {
            localStorage.removeItem('user');
          }
          
          window.dispatchEvent(new Event('localStorageChange'));
          
          ElMessage.success('登录成功');
          
          const redirectPath = getRedirectPath();
          // 统一使用 router.push 进行跳转
          router.push(redirectPath).catch(err => {
            // 如果路由跳转失败，使用 window.location 作为备选方案
            console.warn('路由跳转失败，使用 window.location:', err);
            window.location.href = redirectPath;
          });
        } else {
          ElMessage.error(response.message || '登录失败');
        }
      } catch (error) {
        console.error('登录错误:', error);
        ElMessage.error('登录失败，请检查用户名和密码');
      } finally {
        passwordLoading.value = false;
      }
    };
    
    // 处理来自回调页面的 postMessage
    const handlePostMessage = (event) => {
      // 记录所有 postMessage，便于调试
      console.log('📨 收到 postMessage:', {
        origin: event.origin,
        currentOrigin: window.location.origin,
        data: event.data,
        source: event.source,
        type: event.data?.type
      });
      
      // 检查消息类型
      if (event.data && event.data.type === 'wechat_scan_login_success') {
        const { token, user } = event.data;
        
        console.log('✅ 收到微信扫码登录成功消息:', { hasToken: !!token, hasUser: !!user });
        
        // 直接使用 postMessage 中的 token 和用户信息，不依赖 token 匹配
        if (token) {
          console.log('✅ postMessage 收到登录成功消息，直接使用 token 和用户信息');
          
          // 停止轮询
          if (statusPollingTimer) {
            clearInterval(statusPollingTimer);
            statusPollingTimer = null;
          }
          
          // 先保存 token 和用户信息
          localStorage.setItem('token', token);
          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            console.log('✅ 用户信息已保存:', user);
          } else {
            localStorage.removeItem('user');
          }
          
          // 触发 localStorageChange 事件，通知 App.vue 更新状态
          window.dispatchEvent(new Event('localStorageChange'));
          
          // 等待 App.vue 的 updateUserInfo 完成，确保用户信息已加载
          // 这样可以避免 EmotionMarket 组件挂载时 getCurrentUser 失败
          setTimeout(() => {
            ElMessage.success('微信登录成功');
            
            // 扫码登录统一直接跳转到表情页面（使用 router.push 避免整页重载）
            console.log('🚀 postMessage 开始跳转到 /market，当前 token:', localStorage.getItem('token') ? '已保存' : '未保存');
            router.push('/market').catch(err => {
              console.warn('路由跳转失败，使用 window.location:', err);
              window.location.href = '/market';
            });
          }, 500);
        } else {
          console.warn('⚠️ postMessage 收到登录成功消息，但缺少 token');
        }
      } else {
        console.log('📨 收到其他类型的 postMessage，忽略');
      }
    };
    
    onMounted(() => {
      // 如果已经登录，不生成二维码，直接跳转
      const existingToken = localStorage.getItem('token');
      if (existingToken) {
        console.log('✅ 检测到已有 token，跳过二维码生成，直接跳转');
        router.push('/market').catch(err => {
          console.warn('路由跳转失败，使用 window.location:', err);
          window.location.href = '/market';
        });
        return;
      }
      
      // 自动生成二维码
      generateQrcode();
      
      // 监听来自回调页面的 postMessage
      window.addEventListener('message', handlePostMessage);
    });
    
    onUnmounted(() => {
      // 清理定时器
      if (statusPollingTimer) {
        clearInterval(statusPollingTimer);
      }
      
      // 移除事件监听
      window.removeEventListener('message', handlePostMessage);
    });
    
    return {
      qrcodeLoading,
      scanStatus,
      scanHint,
      User,
      Lock,
      CircleCheck,
      passwordForm,
      passwordRules,
      passwordLoading,
      passwordFormRef,
      generateQrcode,
      refreshQrcode,
      handlePasswordLogin
    };
  }
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.login-wrapper {
  display: flex;
  width: 100%;
  max-width: 1000px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

/* 左侧：微信扫码登录 */
.login-left {
  flex: 1;
  padding: 40px 40px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background: #fafafa;
  border-right: 1px solid #e8e8e8;
  padding-top: 80px;
}

.qr-section {
  text-align: center;
  width: 100%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.login-title {
  font-size: 32px;
  font-weight: 600;
  color: #333;
  margin: 0 0 10px 0;
}

.login-subtitle {
  font-size: 14px;
  color: #1890ff;
  margin: 0 0 40px 0;
}

.qrcode-container {
  margin-bottom: 20px;
}

.qrcode-loading {
  padding: 60px 20px;
  color: #999;
}

.qrcode-loading .el-icon {
  font-size: 40px;
  margin-bottom: 10px;
}

.qrcode-wrapper {
  display: inline-block;
}

.qrcode-image {
  width: 240px;
  height: 240px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  display: block;
  margin: 0 auto 15px;
}

.open-qrcode-btn {
  width: 100%;
  max-width: 300px;
  margin: 0 auto 15px;
  display: block;
  height: 50px;
  font-size: 16px;
}

.qrcode-tip {
  font-size: 14px;
  color: #666;
  margin: 10px 0 0 0;
}

.qrcode-tip.scanned {
  color: #1890ff;
}

.qrcode-tip.expired {
  color: #ff4d4f;
}

.qrcode-placeholder {
  padding: 60px 20px;
  color: #999;
}

.qrcode-placeholder .el-icon {
  font-size: 60px;
  margin-bottom: 10px;
}

.refresh-btn {
  color: #1890ff;
  font-size: 14px;
}

/* 微信二维码容器 */
.wx-iframe-container {
  width: 300px;
  height: 300px;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  margin: 0 auto 15px;
  position: relative;
}

/* 确保微信 SDK 生成的 iframe 正确显示 */
.wx-iframe-container :deep(iframe) {
  width: 100% !important;
  height: 100% !important;
  border: none;
}

/* 状态提示 */
.qr-status-tips {
  margin-top: 10px;
  text-align: center;
}

.qr-status-tips .tip {
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  margin: 5px 0;
}

.qr-status-tips .tip.success {
  color: #67c23a;
}

.qr-status-tips .tip.error {
  color: #f56c6c;
}

.qr-status-tips .link {
  color: #409eff;
  cursor: pointer;
  text-decoration: underline;
}

.qr-status-tips .link:hover {
  color: #66b1ff;
}

/* 右侧：账号登录表单 */
.login-right {
  flex: 1;
  padding: 60px 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-section {
  width: 100%;
  max-width: 400px;
}

.form-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0 0 30px 0;
  text-align: center;
}

.login-form {
  margin-top: 20px;
}

.login-form :deep(.el-form-item) {
  margin-bottom: 24px;
}

.login-form :deep(.el-input__wrapper) {
  padding: 12px 16px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .login-wrapper {
    flex-direction: column;
  }
  
  .login-left {
    border-right: none;
    border-bottom: 1px solid #e8e8e8;
    padding: 40px 20px;
  }
  
  .login-right {
    padding: 40px 20px;
  }
}
</style>
